import { useState } from "react";
import { SelectInput } from "~/components/SelectInput";
import {
    AssetTimeseriesColumns,
    AssetTimeseriesGroupBy,
} from "~/modules/asset_timeseries_v4/types";
import type { ScreenerAsset } from "~/modules/asset_screener/pages/listing/types";
import { FilterOperators } from "~/modules/filters/types";
import {
    MetricTypes as YieldMetricTypes,
    YieldTimeseriesChart,
} from "~/modules/visualization/components/YieldTimeseriesChart";
import { RWA_COLORS } from "~/styles/colors";
import {
    HIGH_YIELD_ASSET_CLASS_OPTIONS,
    HIGH_YIELD_OVERVIEW_VALUE,
} from "./consts";

// // // //

export function HomeHighYieldChart(props: {
    assets: ScreenerAsset[];
    creditYieldAssetIDs: number[];
}) {
    const { assets, creditYieldAssetIDs } = props;
    const [assetClassSelection, setAssetClassSelection] = useState(
        HIGH_YIELD_OVERVIEW_VALUE,
    );
    const isOverview = assetClassSelection === HIGH_YIELD_OVERVIEW_VALUE;
    const assetClassID = isOverview ? null : Number(assetClassSelection);
    const selectedAssetClass = assetClassID
        ? HIGH_YIELD_ASSET_CLASS_OPTIONS.find(
              (option) => option.id === assetClassID,
          )
        : null;
    const selectedAssetClassColor = assetClassID
        ? (assets.find((asset) => asset.assetClass.id === assetClassID)
              ?.assetClass.color_hex ?? RWA_COLORS.blue[600])
        : undefined;

    return (
        <YieldTimeseriesChart
            title="Tokenized High Yield"
            infoTooltip="Overview compares each qualifying asset class's average daily 30-day APY. Select an asset class to view its unsmoothed individual assets in gray and its equal-weighted average in color. Averages use a trailing 7-calendar-day simple moving average. Qualifying assets have at least $10 million in current circulating asset value; assets with implausible, zero-heavy, or stale APY histories are excluded after manual data-quality review."
            headerControls={
                <SelectInput
                    className="w-full sm:w-48"
                    name="high-yield-asset-class"
                    placeholder="Constituent Breakdown"
                    size="xs"
                    value={assetClassSelection}
                    onChange={setAssetClassSelection}
                    optionsContainerAlign="end"
                    options={[
                        {
                            value: HIGH_YIELD_OVERVIEW_VALUE,
                            content: { label: "All asset classes" },
                        },
                        ...HIGH_YIELD_ASSET_CLASS_OPTIONS.map((option) => ({
                            value: String(option.id),
                            content: { label: option.label },
                        })),
                    ]}
                    testID="high-yield-asset-class-select"
                />
            }
            dataSeriesName={
                selectedAssetClass
                    ? `${selectedAssetClass.label} Average`
                    : undefined
            }
            dataSeriesColor={selectedAssetClassColor}
            trailingAverageDays={7}
            lineWidth={1.5}
            showAverageWithConstituents={!isOverview}
            showControls={false}
            analyticsChartName="home-average-credit-active-strategies-yield"
            groupBy={
                isOverview
                    ? AssetTimeseriesGroupBy.assetClass
                    : AssetTimeseriesGroupBy.asset
            }
            filters={[
                {
                    operator: FilterOperators.Or,
                    filters: creditYieldAssetIDs.map((assetID) => ({
                        field: AssetTimeseriesColumns.assetID,
                        operator: FilterOperators.Equals,
                        value: assetID,
                    })),
                },
                ...(assetClassID
                    ? [
                          {
                              field: AssetTimeseriesColumns.assetClassID,
                              operator: FilterOperators.Equals,
                              value: assetClassID,
                          },
                      ]
                    : []),
            ]}
            controls={{
                legend: {
                    enabled: true,
                    active: true,
                    align: "center",
                    layout: "horizontal",
                    verticalAlign: "bottom",
                    itemWidth: 220,
                },
                metrics: {
                    options: [YieldMetricTypes.Apy30Day],
                },
                navigator: {
                    enabled: false,
                    active: false,
                },
            }}
            columnSpanConfig={{
                sm: 12,
                md: 12,
                lg: 12,
                xl: 6,
                xxl: 6,
            }}
        />
    );
}
